import {
  Component,
  computed,
  effect,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { isSameDate, cloneDate } from '../../../../core/utils/date';
import { parseDate } from '../../../../core/utils/parser';
import {
  hasDateIncongruence,
  getMinYear,
  getMaxYear,
  getSafeMinDate,
  getSafeMaxDate,
  isWithinRange,
} from './calendar-form-control-helper';
import { DropdownFormControl } from '../dropdown-form-control/dropdown-form-control';
import { DropdownOption } from '../../../models/form-controls.model';
import { DateRange } from '../../../models/date-range.model';
import { ArrowLeftOutline } from '../../icons/arrow-left-outline';
import { ArrowRightOutline } from '../../icons/arrow-right-outline';
import { IconSolidButton } from '../../buttons/icon-solid-button/icon-solid-button';

/**
 * Interfaz que representa un día en el calendario con todos sus posibles estados
 */
interface CalendarDay {
  /** Fecha completa del día */
  date: Date;
  /** Número del día visible (1-31) */
  label: number;
  /**
   * Indica si el día pertenece al mes actualmente visualizado
   * false para días del mes anterior/siguiente que se muestran en gris
   */
  inMonth: boolean;
  /** Indica si el día es la fecha actual (hoy) */
  isToday: boolean;
  /**
   * Indica si el día está seleccionado en modo individual
   * Solo aplica cuando mode='single'
   */
  isSelected: boolean;
  /**
   * Indica si el día es el punto de inicio de un rango seleccionado
   * Aplica solo cuando mode='range' y hay una fecha de inicio
   */
  isRangeStart: boolean;
  /**
   * Indica si el día es el punto final de un rango seleccionado
   * Aplica solo cuando mode='range' y hay una fecha de fin
   */
  isRangeEnd: boolean;
  /**
   * Indica si el día está dentro del rango (excluyendo extremos)
   * Días intermedios entre start y end, no incluye los límites
   * Aplica solo cuando mode='range' y el rango está completo
   */
  isInRange: boolean;
  /**
   * Indica si el día está deshabilitado y no puede ser seleccionado
   * Se aplica por límites minDate/maxDate o por lógica de negocio
   */
  disabled: boolean;
}

/**
 * Componente de calendario con soporte para selección individual y por rango
 *
 * @example
 * ```html
 * <!-- Modo individual -->
 * <calendar-form-control
 *   [mode]="'single'"
 *   [(selectedDate)]="myDate" />
 *
 * <!-- Modo rango -->
 * <calendar-form-control
 *   [mode]="'range'"
 *   [(selectedRange)]="myDateRange" />
 * ```
 */
@Component({
  selector: 'calendar-form-control',
  imports: [IconSolidButton, DropdownFormControl],
  templateUrl: './calendar-form-control.html',
  styleUrl: './calendar-form-control.scss',
})
export class CalendarFormControl implements OnInit {
  // ==================== INPUTS & MODELS ====================
  /** Fecha mínima permitida para selección */
  minDate = input<Date | null>(null);
  /** Fecha máxima permitida para selección */
  maxDate = input<Date | null>(null);
  /** Fecha seleccionada (modo single) */
  selectedDate = model<Date | null>(null);
  /** Rango seleccionado (modo range) */
  selectedRange = model<DateRange | null>(null);
  /** Modo de selección: 'single' o 'range' */
  mode = input<'single' | 'range'>('single');

  // ==================== INTERNAL SIGNALS ====================
  /** Fecha actual que se está visualizando en el calendario */
  readonly viewDate = signal<Date>(new Date());

  /**
   * Fecha mínima normalizada (parseada) con valor por defecto del calendar
   */
  private readonly normalizedMinDate = computed(() => {
    const parsed = parseDate(this.minDate());
    return getSafeMinDate(parsed ?? undefined);
  });

  /**
   * Fecha máxima normalizada (parseada) con valor por defecto del calendar
   */
  private readonly normalizedMaxDate = computed(() => {
    const parsed = parseDate(this.maxDate());
    return getSafeMaxDate(parsed ?? undefined);
  });

  /**
   * Fecha seleccionada normalizada (parseada)
   */
  private readonly normalizedSelectedDate = computed(() => {
    return parseDate(this.selectedDate());
  });

  /**
   * Rango seleccionado normalizado (parseado)
   */
  private readonly normalizedSelectedRange = computed(() => {
    const range = this.selectedRange();
    return {
      start: parseDate(range?.start),
      end: parseDate(range?.end),
    };
  });

  private readonly allMonths: readonly DropdownOption<number>[] = Object.freeze(
    [
      { label: 'Enero ', value: 0 },
      { label: 'Febrero', value: 1 },
      { label: 'Marzo', value: 2 },
      { label: 'Abril', value: 3 },
      { label: 'Mayo', value: 4 },
      { label: 'Junio', value: 5 },
      { label: 'Julio', value: 6 },
      { label: 'Agosto', value: 7 },
      { label: 'Septiembre', value: 8 },
      { label: 'Octubre', value: 9 },
      { label: 'Noviembre', value: 10 },
      { label: 'Diciembre', value: 11 },
    ]
  );

  ArrowLeftOutline = ArrowLeftOutline;
  ArrowRightOutline = ArrowRightOutline;

  /**
   * Inicializa el componente estableciendo la vista inicial
   */
  ngOnInit(): void {
    this.viewDate.set(this.getInitialViewDate());
  }

  /**
   * Determina la fecha inicial para la vista del calendario
   * @returns Fecha inicial (usa minDate si existe, sino fecha actual)
   */
  private getInitialViewDate(): Date {
    const rangeStart = this.normalizedSelectedRange().start;
    const selected = this.normalizedSelectedDate();

    if (this.mode() === 'range' && rangeStart) {
      return rangeStart;
    }
    if (this.mode() === 'single' && selected) {
      return selected;
    }

    return this.minDate() ? this.normalizedMinDate() : new Date();
  }

  /**
   * Actualiza la fecha de vista del calendario
   * @param year Año a establecer
   * @param month Mes a establecer (0-11)
   */
  private updateViewDate(year: number, month: number): void {
    const newDate = new Date(year, month, 1);
    this.viewDate.set(newDate);
  }

  /**
   * Efecto que sincroniza la vista con la selección actual
   * Mueve la vista al mes de la fecha seleccionada o al inicio del rango
   */
  private readonly syncSelectedToViewEffect = effect(() => {
    const targetDate =
      this.mode() === 'range'
        ? this.normalizedSelectedRange().start
        : this.normalizedSelectedDate();

    if (targetDate) {
      this.viewDate.set(targetDate);
    }
  });

  /**
   * Calcula si hay incongruencia entre las fechas mínima y máxima
   * @returns true si maxDate < minDate
   */
  hasIncongruence = computed(() => {
    return hasDateIncongruence(
      this.normalizedMinDate(),
      this.normalizedMaxDate()
    );
  });

  /**
   * Determina si el botón de navegación al mes anterior está deshabilitado
   * @returns true si no se puede navegar al mes anterior
   */
  prevDisabled = computed<boolean>(() => {
    if (this.hasIncongruence()) return true;

    const viewDate = this.viewDate();
    const min = new Date(
      this.normalizedMinDate().getFullYear(),
      this.normalizedMinDate().getMonth(),
      1
    );

    const currentMonth = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth(),
      1
    );
    return currentMonth <= min;
  });

  /**
   * Determina si el botón de navegación al mes siguiente está deshabilitado
   * @returns true si no se puede navegar al mes siguiente
   */
  nextDisabled = computed<boolean>(() => {
    if (this.hasIncongruence()) return true;

    const viewDate = this.viewDate();
    const max = new Date(
      this.normalizedMaxDate().getFullYear(),
      this.normalizedMaxDate().getMonth(),
      1
    );

    const currentMonth = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth(),
      1
    );
    return currentMonth >= max;
  });

  /**
   * Calcula la fecha correspondiente a una celda del calendario
   * @param i Índice de la celda (0-41)
   * @param startIndex Índice de inicio del mes actual
   * @param year Año de la vista
   * @param month Mes de la vista (0-11)
   * @param daysInMonth Número de días del mes
   * @param prevMonthDays Número de días del mes anterior
   * @returns Fecha correspondiente a la celda
   */
  private getCellDate(
    i: number,
    startIndex: number,
    year: number,
    month: number,
    daysInMonth: number,
    prevMonthDays: number
  ): Date {
    if (i < startIndex) {
      const day = prevMonthDays - (startIndex - i - 1);
      return new Date(year, month - 1, day);
    } else if (i < startIndex + daysInMonth) {
      const day = i - startIndex + 1;
      return new Date(year, month, day);
    } else {
      const day = i - (startIndex + daysInMonth) + 1;
      return new Date(year, month + 1, day);
    }
  }

  /**
   * Determina el estado de rango de una fecha
   * @param cellDate Fecha a evaluar
   * @param range Rango seleccionado
   * @returns Objeto con el estado de rango de la fecha
   */
  private getRangeStatus(cellDate: Date, range: DateRange) {
    return {
      isRangeStart: range.start ? isSameDate(cellDate, range.start) : false,
      isRangeEnd: range.end ? isSameDate(cellDate, range.end) : false,
      isInRange: !!(
        range.start &&
        range.end &&
        cellDate > range.start &&
        cellDate < range.end
      ),
    };
  }

  /**
   * Genera la cuadrícula de días del calendario
   * @returns Arreglo de 42 días (6 semanas × 7 días)
   */
  daysGrid = computed<CalendarDay[]>(() => {
    const grid: CalendarDay[] = [];
    const totalCells = 42;

    const view = this.viewDate();
    const year = view.getFullYear();
    const month = view.getMonth();

    const today = new Date();
    const selected = this.normalizedSelectedDate();
    const range = this.normalizedSelectedRange();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();
    const startIndex = (firstDay.getDay() + 6) % 7;

    for (let i = 0; i < totalCells; i++) {
      const cellDate = this.getCellDate(
        i,
        startIndex,
        year,
        month,
        daysInMonth,
        prevMonthDays
      );
      const inMonth = i >= startIndex && i < startIndex + daysInMonth;
      const rangeStatus = this.getRangeStatus(cellDate, range);

      grid.push({
        date: cellDate,
        inMonth,
        label: cellDate.getDate(),
        isToday: isSameDate(cellDate, today),
        isSelected: selected ? isSameDate(cellDate, selected) : false,
        ...rangeStatus,
        disabled: !isWithinRange(
          cellDate,
          this.normalizedMinDate(),
          this.normalizedMaxDate()
        ),
      });
    }

    return grid;
  });

  /**
   * Genera las opciones de años disponibles para el selector
   * @returns Arreglo de opciones de años dentro del rango permitido
   */
  yearsOptions = computed<DropdownOption<number>[]>(() => {
    if (this.hasIncongruence()) {
      const now = new Date();
      return [{ label: `${now.getFullYear()}`, value: now.getFullYear() }];
    }

    const minYear = getMinYear(this.normalizedMinDate());
    const maxYear = getMaxYear(this.normalizedMaxDate());

    const years: DropdownOption<number>[] = [];
    const yearSet = new Set<number>();

    for (let year = minYear; year <= maxYear; year++) {
      years.push({ label: `${year}`, value: year });
      yearSet.add(year);
    }

    const selected = this.normalizedSelectedDate();
    if (selected) {
      const selectedYear = selected.getFullYear();
      if (!yearSet.has(selectedYear)) {
        years.push({ label: `${selectedYear}`, value: selectedYear });
        yearSet.add(selectedYear);
      }
    }

    const range = this.normalizedSelectedRange();
    if (range.start) {
      const startYear = range.start.getFullYear();
      if (!yearSet.has(startYear)) {
        years.push({ label: `${startYear}`, value: startYear });
        yearSet.add(startYear);
      }
    }
    if (range.end) {
      const endYear = range.end.getFullYear();
      if (!yearSet.has(endYear)) {
        years.push({ label: `${endYear}`, value: endYear });
        yearSet.add(endYear);
      }
    }

    return years.sort((a, b) => a.value - b.value);
  });

  /**
   * Genera las opciones de meses disponibles para el selector
   * @returns Arreglo de opciones de meses dentro del rango permitido
   */
  monthsOptions = computed<DropdownOption<number>[]>(() => {
    if (this.hasIncongruence()) {
      const month = this.viewDate().getMonth();
      return this.allMonths.filter(m => m.value === month);
    }

    const currentYear = this.viewDate().getFullYear();
    const min = this.normalizedMinDate();
    const max = this.normalizedMaxDate();

    if (currentYear < min.getFullYear() || currentYear > max.getFullYear()) {
      const currentMonth = this.viewDate().getMonth();
      return this.allMonths.filter(m => m.value === currentMonth);
    }

    let startMonth = 0;
    let endMonth = 11;

    if (min && min.getFullYear() === currentYear) startMonth = min.getMonth();
    if (max && max.getFullYear() === currentYear) endMonth = max.getMonth();

    return this.allMonths.filter(
      month => +month.value >= startMonth && +month.value <= endMonth
    );
  });

  /**
   * Actualiza el mes seleccionado en el calendario
   * @param month Nuevo mes (0-11)
   */
  updateMonth(month: number | null) {
    if (month === null) return;
    const year = this.viewDate().getFullYear();
    this.updateViewDate(year, month);
  }

  /**
   * Actualiza el año seleccionado en el calendario
   * @param year Nuevo año
   */
  updateYear(year: number | null) {
    if (year === null) return;
    const currentMonth = this.viewDate().getMonth();
    const min = this.normalizedMinDate();
    const max = this.normalizedMaxDate();

    let targetMonth = currentMonth;

    if (year === min.getFullYear() && currentMonth < min.getMonth()) {
      targetMonth = min.getMonth();
    }

    if (year === max.getFullYear() && currentMonth > max.getMonth()) {
      targetMonth = max.getMonth();
    }

    this.updateViewDate(year, targetMonth);
  }

  /**
   * Navega al mes anterior o siguiente
   * @param offset Dirección de navegación: -1 para anterior, 1 para siguiente
   */
  changeMonth(offset: 1 | -1) {
    if (this.hasIncongruence()) return;

    const viewDate = this.viewDate();
    const min = new Date(
      this.normalizedMinDate().getFullYear(),
      this.normalizedMinDate().getMonth(),
      1
    );
    const max = new Date(
      this.normalizedMaxDate().getFullYear(),
      this.normalizedMaxDate().getMonth(),
      1
    );
    const currentMonth = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth(),
      1
    );
    if (offset === 1) {
      if (currentMonth < min) {
        this.viewDate.set(min);

        return;
      }

      if (currentMonth >= max) {
        return;
      }
    } else {
      if (currentMonth <= min) {
        return;
      }

      if (currentMonth > max) {
        this.viewDate.set(max);
        return;
      }
    }

    const newDate = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth() + offset,
      1
    );

    this.viewDate.set(newDate);
  }

  /**
   * Maneja la selección de un día en el calendario
   * @param day Información del día seleccionado
   */
  selectDay(day: CalendarDay) {
    if (day.disabled) return;

    const selectedDate = cloneDate(day.date);

    if (!day.inMonth) {
      this.viewDate.set(
        new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
      );
    }

    if (this.mode() === 'range') {
      this.handleRangeSelection(selectedDate);
    } else {
      this.selectedDate.set(selectedDate);
    }
  }

  /**
   * Maneja la lógica de selección de rango
   * @param selectedDate Fecha seleccionada
   */
  private handleRangeSelection(selectedDate: Date) {
    const currentRange = this.selectedRange();

    if (!currentRange?.start || (currentRange?.start && currentRange?.end)) {
      this.selectedRange.set({ start: selectedDate, end: null });
    } else {
      const start =
        selectedDate < currentRange.start ? selectedDate : currentRange.start;
      const end =
        selectedDate < currentRange.start ? currentRange.start : selectedDate;
      this.selectedRange.set({ start, end });
    }
  }
}
